import { Check, ChevronRight, ShieldCheck } from "lucide-react";
import { PublicNavigationLink } from "@/components/layout/PublicNavigationLink";
import { useTranslation } from "react-i18next";
import { usePlatform } from "@/hooks/usePlatform";
import { getPromiseContent } from "@/content/promise";
import { cn } from "@/lib/utils";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { RENTER_PROMISE_FLAG } from "@workspace/feature-flags-shared";
import { Dialog, DialogTrigger } from "@workspace/ui/components/ui/dialog";
import { PromiseDetailsDialog } from "./PromiseDetailsDialog";
import "./PromiseWidget.css";

interface PromiseWidgetProps {
  className?: string;
  mobile?: boolean;
  compact?: boolean;
}

export function PromiseWidget({ className, mobile = false, compact = false }: PromiseWidgetProps) {
  const { t } = useTranslation();
  const platform = usePlatform();
  const promiseEnabled = useFeatureFlag(RENTER_PROMISE_FLAG);
  const content = getPromiseContent(t, platform.name);
  if (!promiseEnabled) return null;
  if (mobile && compact) {
    return (
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label={content.title}
            title={content.title}
            className={cn("car-promise-shield inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-background text-brand hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring", className)}
            data-testid="button-mobile-promise"
          >
            <ShieldCheck className="h-6 w-6" aria-hidden="true" />
          </button>
        </DialogTrigger>
        <PromiseDetailsDialog />
      </Dialog>
    );
  }
  if (mobile) {
    return (
      <aside
        aria-labelledby="mobile-promise-title"
        data-testid="promise-widget-mobile"
        className={cn("car-promise-mobile mt-2 min-w-0 border-t border-border pt-3", className)}
      >
        <PublicNavigationLink
          href="/promise"
          className="grid min-h-11 grid-cols-[24px_minmax(0,1fr)_16px] items-start gap-x-3 gap-y-1 rounded-lg px-1 py-2 text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <ShieldCheck className="h-6 w-6 shrink-0 text-brand" aria-hidden="true" />
          <span className="car-promise-copy min-w-0 flex-1">
            <strong id="mobile-promise-title" className="block text-sm font-semibold leading-5">
              {content.widgetTitle}
            </strong>
            <span className="mt-1 block text-xs leading-5 text-muted-foreground">
              {content.widgetBenefits.slice(0, 3).join(" · ")}
            </span>
          </span>
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <span className="car-promise-copy col-start-2 min-w-0 text-xs font-semibold leading-5 text-brand">{t("promise.widget_link")}</span>
        </PublicNavigationLink>
      </aside>
    );
  }

  return (
    <aside
      aria-label={content.title}
      data-testid="promise-widget-desktop"
      className={cn(
        "car-promise-card mt-4 min-w-0 rounded-2xl bg-card p-4 text-card-foreground shadow-sm",
        className,
      )}
    >
      <div className="car-promise-layout">
        <PublicNavigationLink
            href="/promise"
            className="flex min-w-0 flex-col items-center rounded-xl px-1 py-2 text-center text-foreground transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={t("promise.widget_link")}
          >
          <ShieldCheck className="h-10 w-10 text-brand" aria-hidden="true" />
          <h2 id="desktop-promise-title" className="car-promise-title mt-2">
            {content.widgetTitle}
          </h2>
          <p className="car-promise-attribution mt-2 text-[10px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
            {t("promise_widget.by_brand", { brand: platform.name })}
          </p>
        </PublicNavigationLink>

        <ul className="car-promise-benefits min-w-0 space-y-2">
          {content.widgetBenefits.map((benefit) => (
            <li key={benefit} className="flex min-w-0 items-start gap-1.5 text-xs font-medium leading-4 text-foreground">
              <Check className="mt-px h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
              <span className="car-promise-copy min-w-0">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}